/**
 * El cliente de Base44 que usan sus pantallas, sustituido.
 *
 * Mantiene la forma exacta que su código espera —`base44.entities.X.get`,
 * `base44.functions.invoke`, `base44.auth.me`— pero habla con nuestra API
 * (`/api/v1/metodo`), con el token de la sesión de la aplicación. Así sus
 * pantallas funcionan sin tocarlas.
 *
 * Dos comportamientos del SDK original que su código da por hechos y aquí se
 * conservan:
 * - Las entidades devuelven los datos directamente y lanzan ante un error
 *   (`get(id).catch(() => null)` es su forma de preguntar si algo existe).
 * - `functions.invoke` devuelve la respuesta entera (`{ data, status }`) y
 *   lanza si el código no es 2xx, con la respuesta en `error.response`, como
 *   hacía axios. Sus pantallas leen `error.response.data.reason_code`.
 */

import { authHeaders } from "../../lib/auth";

const BASE = "/api/v1/metodo";

async function pedir(ruta, opciones = {}) {
  const r = await fetch(BASE + ruta, {
    ...opciones,
    headers: { "content-type": "application/json", ...authHeaders(), ...(opciones.headers || {}) },
  });
  const texto = await r.text();
  let data = null;
  try {
    data = texto ? JSON.parse(texto) : null;
  } catch {
    data = texto;
  }
  return { data, status: r.status, headers: Object.fromEntries(r.headers.entries()) };
}

export class Base44Error extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "Base44Error";
    this.status = status;
    this.data = data;
  }
}

function datosO(res, que) {
  if (res.status < 200 || res.status >= 300) {
    throw new Base44Error(res.data?.message || `${que} respondió ${res.status}`, res.status, res.data);
  }
  return res.data;
}

function entidad(nombre) {
  const ruta = `/entidades/${encodeURIComponent(nombre)}`;
  return {
    async get(id) {
      return datosO(await pedir(`${ruta}/${encodeURIComponent(id)}`), `${nombre}.get`);
    },
    async filter(consulta, orden, limite, saltar) {
      const p = new URLSearchParams({ q: JSON.stringify(consulta || {}) });
      if (orden) p.set("sort", orden);
      if (limite) p.set("limit", String(limite));
      if (saltar) p.set("skip", String(saltar));
      return datosO(await pedir(`${ruta}?${p}`), `${nombre}.filter`);
    },
    list(orden, limite, saltar) {
      return this.filter({}, orden, limite, saltar);
    },
    async create(datos) {
      return datosO(await pedir(ruta, { method: "POST", body: JSON.stringify(datos) }), `${nombre}.create`);
    },
    async update(id, datos) {
      return datosO(
        await pedir(`${ruta}/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(datos) }),
        `${nombre}.update`,
      );
    },
    async delete(id) {
      return datosO(await pedir(`${ruta}/${encodeURIComponent(id)}`, { method: "DELETE" }), `${nombre}.delete`);
    },
  };
}

// La identidad no cambia durante la visita: se pide una vez y se reutiliza.
// Si falla, se olvida para que el siguiente intento vuelva a preguntar.
let identidad = null;

export const base44 = {
  // Cualquier nombre es una entidad: las que existen las decide el servidor.
  /** @type {Record<string, ReturnType<typeof entidad>>} */
  entities: new Proxy({}, {
    get: (_, nombre) => (typeof nombre === "string" && nombre !== "then" ? entidad(nombre) : undefined),
  }),
  functions: {
    async invoke(nombre, datos = {}) {
      const res = await pedir(`/funciones/${encodeURIComponent(nombre)}`, {
        method: "POST",
        body: JSON.stringify(datos),
      });
      if (res.status < 200 || res.status >= 300) {
        const error = new Error(`La función ${nombre} respondió ${res.status}`);
        error.status = res.status;
        error.response = res;
        throw error;
      }
      return res;
    },
  },
  auth: {
    me() {
      identidad ??= pedir("/yo").then((res) => datosO(res, "auth.me")).catch((e) => {
        identidad = null;
        throw e;
      });
      return identidad;
    },
    async isAuthenticated() {
      try {
        await this.me();
        return true;
      } catch {
        return false;
      }
    },
  },
};

/** Para entregar el diagnóstico y abrir el Mapa desde nuestras pantallas. */
export async function entrarEnElMetodo() {
  return pedir("/entrar", { method: "POST", body: "{}" });
}
